/**
 * Bake watermark onto every PDF page and rebuild a downloadable PDF.
 * Pipeline: pdf.js raster → drawWatermarkOnCtx → pdf-lib image pages.
 */

import { PDFDocument } from "pdf-lib";
import { drawWatermarkOnCtx } from "../../../../utils/drawWatermarkOnCanvas";
import {
  loadPdfDocument,
  renderPdfPage,
  PDF_WATERMARK_MAX_PAGES,
} from "./renderPdfFirstPage";

function canvasToPngBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode page image"));
          return;
        }
        blob.arrayBuffer().then(
          (buf) => resolve(new Uint8Array(buf)),
          reject
        );
      },
      "image/png"
    );
  });
}

/** Let the browser paint / handle input so long bakes don't trip "Page not responding". */
function yieldToMain() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function stampedPdfFileName(originalName, suffix = "watermarked") {
  const base =
    String(originalName || "document")
      .replace(/\.pdf$/i, "")
      .trim() || "document";
  const safeSuffix = String(suffix || "watermarked")
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "watermarked";
  return `${base}-${safeSuffix}.pdf`;
}

function resolveBakeWatermarks(watermarkOrResolver, pageNum, totalPages) {
  if (typeof watermarkOrResolver === "function") {
    const resolved = watermarkOrResolver(pageNum, totalPages);
    if (Array.isArray(resolved)) return resolved.filter(Boolean);
    return resolved ? [resolved] : [];
  }
  if (Array.isArray(watermarkOrResolver)) {
    return watermarkOrResolver.filter(Boolean);
  }
  return watermarkOrResolver ? [watermarkOrResolver] : [];
}

/**
 * @param {File} file
 * @param {object | object[] | ((pageNum: number, total: number) => object|object[]|null)} watermarkOrResolver
 *        Static watermark(s), or per-page resolver (return null/disabled to skip drawing).
 * @param {{ scale?: number, maxPages?: number, fileNameSuffix?: string, onProgress?: (p: { page: number, total: number }) => void }} [opts]
 * @returns {Promise<{ blob: Blob, pageCount: number, fileName: string }>}
 */
export async function bakeWatermarkedPdf(file, watermarkOrResolver, opts = {}) {
  if (!file) throw new Error("No PDF file");

  const isResolver = typeof watermarkOrResolver === "function";
  const isList = Array.isArray(watermarkOrResolver);
  if (
    !isResolver &&
    !isList &&
    !watermarkOrResolver?.enabled
  ) {
    throw new Error("Watermark is disabled");
  }
  if (
    isList &&
    !watermarkOrResolver.some((wm) => wm?.enabled)
  ) {
    throw new Error("Watermark is disabled");
  }

  const scale = opts.scale ?? 1.5;
  const maxPages = opts.maxPages ?? PDF_WATERMARK_MAX_PAGES;
  const onProgress =
    typeof opts.onProgress === "function" ? opts.onProgress : null;
  const fileNameSuffix = opts.fileNameSuffix || "watermarked";

  const pdf = await loadPdfDocument(file);
  const totalPages = Math.min(pdf.numPages || 1, maxPages);

  try {
    const out = await PDFDocument.create();

    for (let pageNum = 1; pageNum <= totalPages; pageNum += 1) {
      onProgress?.({ page: pageNum, total: totalPages });

      const watermarks = resolveBakeWatermarks(
        watermarkOrResolver,
        pageNum,
        totalPages
      );

      const { canvas, width, height } = await renderPdfPage(pdf, pageNum, {
        scale,
        toDataUrl: false,
      });
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas unavailable");

      for (const watermark of watermarks) {
        if (!watermark?.enabled) continue;
        await drawWatermarkOnCtx(ctx, {
          watermark: { ...watermark, enabled: true },
          stageWidth: width,
          stageHeight: height,
          forceBake: true,
        });
      }

      const pngBytes = await canvasToPngBytes(canvas);
      const embedded = await out.embedPng(pngBytes);
      const page = out.addPage([width, height]);
      page.drawImage(embedded, {
        x: 0,
        y: 0,
        width,
        height,
      });

      // Free canvas memory promptly on long documents.
      canvas.width = 0;
      canvas.height = 0;

      await yieldToMain();
    }

    const pdfBytes = await out.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const fileName = stampedPdfFileName(file.name, fileNameSuffix);

    return { blob, pageCount: totalPages, fileName };
  } finally {
    try {
      pdf.cleanup?.();
      await pdf.destroy?.();
    } catch {
      // ignore
    }
  }
}

/**
 * Bake + trigger browser download.
 */
export async function downloadWatermarkedPdf(file, watermark, opts = {}) {
  const result = await bakeWatermarkedPdf(file, watermark, opts);
  downloadBlob(result.blob, result.fileName);
  return result;
}
